import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadToDoes,
  loadToDo,
  saveToDo,
  modifyToDo,
  removeToDo,
} from '../api/toDo'
import { CreateToDo, ToDo } from '../types/toDo'

// get toDoes
export const getToDoes = createAsyncThunk(
  'note/getToDoes',
  async (_, thunkApi) => {
    try {
      return await loadToDoes()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// get note
export const getToDo = createAsyncThunk(
  'note/getToDo',
  async (id: number, thunkApi) => {
    try {
      return await loadToDo(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// create note
export const addToDo = createAsyncThunk(
  'note/addToDo',
  async (note: CreateToDo, thunkApi) => {
    try {
      await saveToDo(note)
      return await loadToDoes()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// update note
export const updateToDo = createAsyncThunk(
  'note/updateToDo',
  async (note: ToDo, thunkApi) => {
    try {
      await modifyToDo(note)
      return await loadToDoes()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// delete note
export const deleteToDo = createAsyncThunk(
  'note/deleteToDo',
  async (id: number, thunkApi) => {
    try {
      await removeToDo(id)
      return await loadToDoes()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface ToDoSliceState {
  allToDoes: ToDo[]
  toDoes: ToDo[]
  toDo?: ToDo
  toDoesLoading: boolean
  toDoesError?: SerializedError
}
//FIXME: move each to separate slice // todo, memory, idea
const initialState: ToDoSliceState = {
  allToDoes: [],
  toDoes: [],
  toDo: undefined,
  toDoesLoading: false,
  toDoesError: undefined,
}

const toDoSlice = createSlice({
  name: 'toDoes',
  initialState,
  reducers: {
    sortToDoes: (state: ToDoSliceState, action: PayloadAction<string>) => {
      const High = state.toDoes.filter((note: ToDo) => note.priority === 'High')
      const Medium = state.toDoes.filter(
        (note: ToDo) => note.priority === 'Medium',
      )
      const Low = state.toDoes.filter((note: ToDo) => note.priority === 'Low')
      if (action.payload === 'High') {
        state.toDoes = [...High, ...Medium, ...Low]
      } else if (action.payload === 'Medium') {
        state.toDoes = [...Medium, ...High, ...Low]
      } else if (action.payload === 'Low') {
        state.toDoes = [...Low, ...High, ...Medium]
      } else {
        state.toDoes = state.allToDoes
      }
    },
    filterToDoes: (state: ToDoSliceState, action: PayloadAction<string>) => {
      const sortedToDoes =
        action.payload === 'Default'
          ? state.allToDoes
          : state.allToDoes.filter((note: ToDo) => note.type === action.payload)
      state.toDoes = sortedToDoes
    },
    setToDo: (
      state: ToDoSliceState,
      action: PayloadAction<ToDo | undefined>,
    ) => {
      state.toDo = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getToDoes.fulfilled, (state, action: PayloadAction<ToDo[]>) => {
      state.toDoes = action.payload as any
      state.allToDoes = action.payload as any
      state.toDoesLoading = false
      state.toDoesError = undefined
    })
    builder.addCase(getToDoes.pending, (state) => {
      state.toDoesLoading = true
      state.toDoesError = undefined
    })
    builder.addCase(getToDoes.rejected, (state, action) => {
      state.toDoesLoading = false
      state.toDoesError = action.error
    })
    builder.addCase(addToDo.fulfilled, (state, action: PayloadAction<ToDo[]>) => {
      state.toDoes = action.payload as any
      state.allToDoes = action.payload as any
      state.toDoesLoading = false
      state.toDoesError = undefined
    })
    builder.addCase(addToDo.pending, (state) => {
      state.toDoesLoading = true
      state.toDoesError = undefined
    })
    builder.addCase(addToDo.rejected, (state, action) => {
      state.toDoesLoading = false
      state.toDoesError = action.error
    })
    builder.addCase(updateToDo.fulfilled, (state, action: PayloadAction<ToDo | ToDo[]>) => {
      const payload = action.payload
      if (Array.isArray(payload)) {
        state.toDoes = payload as any
        state.allToDoes = payload as any
      } else if (payload && Array.isArray(state.toDoes)) {
        const next = state.toDoes.map((item) =>
          item.id === payload.id ? { ...item, ...payload } : item,
        )
        state.toDoes = next as any
        state.allToDoes = next as any
      }
      state.toDoesLoading = false
      state.toDoesError = undefined
    })
    builder.addCase(updateToDo.pending, (state) => {
      state.toDoesLoading = true
      state.toDoesError = undefined
    })
    builder.addCase(updateToDo.rejected, (state, action) => {
      state.toDoesLoading = false
      state.toDoesError = action.error
    })
    builder.addCase(deleteToDo.fulfilled, (state, action: PayloadAction<ToDo[]>) => {
      state.toDoes = action.payload as any
      state.allToDoes = action.payload as any
      state.toDoesLoading = false
      state.toDoesError = undefined
    })
    builder.addCase(deleteToDo.pending, (state) => {
      state.toDoesLoading = true
      state.toDoesError = undefined
    })
    builder.addCase(deleteToDo.rejected, (state, action) => {
      state.toDoesLoading = false
      state.toDoesError = action.error
    })
  },
})

export const { sortToDoes, filterToDoes, setToDo } = toDoSlice.actions
export default toDoSlice.reducer
